import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useTeacherOrStudentClassSettings } from 'approot/activity-builder/present/activity-present-settings.hooks';
import prefersReducedMotion from 'approot/shared/ally/prefers-reduced-motion';

export const usePausePlaySettings = () => {
  const { animationAutoPlay } = useTeacherOrStudentClassSettings();

  const shouldAutoPlay = useMemo(
    () => (prefersReducedMotion() ? false : animationAutoPlay),
    [animationAutoPlay]
  );

  const [isPlaying, setIsPlaying] = useState<boolean>(animationAutoPlay);

  useEffect(() => {
    setIsPlaying(shouldAutoPlay);
  }, [shouldAutoPlay]);

  return {
    isPlaying,
    shouldAutoPlay,
    setIsPlaying,
  };
};

type PlayPausePosition = { top: number; left: number } | null;

/**
 * Tracks where a play/pause control should sit relative to an image inside a
 * positioned container (anchored to the image's bottom-left corner, offset by
 * `leftInset`/`topInset`). Returns refs to attach to the container and image
 * plus the computed position, recalculating on image load, container resize,
 * and window resize.
 *
 * Each call manages its own refs and state, so a page can drive multiple
 * independent play/pause images by calling the hook once per image.
 *
 * @param params.leftInset - Horizontal offset (px) from the image's left edge.
 * @param params.topInset - Vertical offset (px) below the image's bottom edge.
 * @returns Refs for the container and image, the current position (or `null`
 *   until it can be measured), and an `onLoad` handler for the image.
 */
export const usePlayPausePosition = ({
  leftInset = 12,
  topInset = 12,
}: { leftInset?: number; topInset?: number } = {}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [playPausePosition, setPlayPausePosition] =
    useState<PlayPausePosition>(null);

  const updatePlayPausePosition = useCallback(() => {
    const container = containerRef.current;
    const image = imageRef.current;
    if (!container || !image) {
      setPlayPausePosition(null);
      return;
    }
    const containerRect = container.getBoundingClientRect();
    const imageRect = image.getBoundingClientRect();
    setPlayPausePosition({
      left: imageRect.left - containerRect.left + leftInset,
      top: imageRect.bottom - containerRect.top + topInset,
    });
  }, [leftInset, topInset]);

  useLayoutEffect(() => {
    updatePlayPausePosition();
    const image = imageRef.current;
    if (image?.complete) {
      updatePlayPausePosition();
    }
    const resizeObserver = new ResizeObserver(updatePlayPausePosition);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    window.addEventListener('resize', updatePlayPausePosition);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updatePlayPausePosition);
    };
  }, [updatePlayPausePosition]);

  const handleImageLoad = useCallback(() => {
    updatePlayPausePosition();
  }, [updatePlayPausePosition]);

  return { containerRef, imageRef, playPausePosition, handleImageLoad };
};
